import type { UseMutationConfig } from 'react-relay';
import { useMutation } from 'react-relay';
import type { MutationParameters, PayloadError } from 'relay-runtime';

import { useToast } from "@lecartte/shared-ui/components/Toast";

export const getError = (data: any) => {
  if (data?.error) {
    return data.error;
  }

  if (data?.errors && data.errors.length > 0) {
    const first = data.errors[0];
    if (typeof first === 'string') {
      return data.errors.join('\n');
    }
    return first?.message ?? 'Erro desconhecido';
  }

  return null;
};

export type UseMutationCallbacksArgs<TMutation extends MutationParameters> = {
  mutation: any;
  name: keyof TMutation['response'];
  success?: string | undefined;
  error?: string;
  shouldShowSnackbar?: boolean;
  batchOperation?: boolean;
  afterCompleted?: (
    response: TMutation['response'],
    errors: Array<PayloadError> | null,
    safeValues?: any | null,
  ) => void | null;
  afterError?: (
    error: string | Error,
    safeValues?: any | null,
    response?: TMutation['response'],
  ) => void;
};

export const useMutationCallbacks = <TMutation extends MutationParameters>(
  args: UseMutationCallbacksArgs<TMutation>,
): [
  (
    executeConfig: UseMutationConfig<TMutation>,
    safeValues?: any | null,
  ) => void,
  boolean,
] => {
  const {
    mutation,
    name,
    afterCompleted,
    afterError,
    shouldShowSnackbar = true,
    batchOperation = false,
  } = args;

  const { showToast } = useToast();
  const [execute, isPending] = useMutation(mutation);

  const onCompleted =
    (error = args.error, success = args.success, safeValues: any | null) =>
    (response: TMutation['response']) => {
      const data = response[name] as any;

      if (!data || data.error || (data.errors && data.errors.length > 0)) {
        const errorMessage = getError(data) || error || 'Ocorreu um erro.';

        if (shouldShowSnackbar) {
          showToast(errorMessage, 'error');
        }

        afterError && afterError(errorMessage, safeValues || {}, response);

        if (!batchOperation) {
          return;
        }
      }

      const successMessage = (typeof data?.success === 'string' ? data.success : null) || success;

      if (successMessage && shouldShowSnackbar) {
        showToast(successMessage, 'success');
      }

      afterCompleted && afterCompleted(response, null, safeValues);
    };

  const onError =
    (error = args.error, safeValues: any | null) =>
    (errorMutation: Error) => {
      if (error && shouldShowSnackbar) {
        showToast(error, 'error');
      }

      afterError && afterError(errorMutation, safeValues);
    };

  const executeFn = (
    executeConfig: UseMutationConfig<TMutation> = { variables: {} },
    safeValues?: any | null,
  ) => {
    const cfg = executeConfig as UseMutationConfig<TMutation> & { error?: string; success?: string };
    return execute({
      ...executeConfig,
      onCompleted: onCompleted(
        cfg.error,
        cfg.success,
        safeValues,
      ),
      onError: onError(cfg.error, safeValues),
    });
  };

  return [executeFn, isPending];
};
